import { Check, ChevronDown } from 'lucide-react-native';
import { useState } from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import type { CommentSortMode } from './comment-threading';

const COMMENT_SORT_OPTIONS: {
  description: string;
  label: string;
  value: CommentSortMode;
}[] = [
  {
    description: 'Newest thread activity first.',
    label: 'Most Recent',
    value: 'recent',
  },
  {
    description: 'Oldest comments first.',
    label: 'Oldest',
    value: 'oldest',
  },
  {
    description: 'Most active threads first.',
    label: 'Most Relevant',
    value: 'relevant',
  },
];

export function labelForCommentSortMode(sortMode: CommentSortMode) {
  return (
    COMMENT_SORT_OPTIONS.find((option) => option.value === sortMode)?.label ??
    'Most Recent'
  );
}

export function CommentSortControl({
  compact = false,
  onChange,
  value,
}: {
  compact?: boolean;
  onChange: (sortMode: CommentSortMode) => void;
  value: CommentSortMode;
}) {
  const [menuVisible, setMenuVisible] = useState(false);
  const activeOption =
    COMMENT_SORT_OPTIONS.find((option) => option.value === value) ??
    COMMENT_SORT_OPTIONS[0];

  function selectSortMode(sortMode: CommentSortMode) {
    setMenuVisible(false);

    if (sortMode !== value) {
      onChange(sortMode);
    }
  }

  return (
    <>
      <Pressable
        accessibilityLabel={`Sort comments: ${activeOption.label}`}
        accessibilityRole="button"
        onPress={() => setMenuVisible(true)}
        style={({ pressed }) => [
          styles.selectButton,
          compact ? styles.selectButtonCompact : null,
          pressed ? styles.selectButtonPressed : null,
        ]}
      >
        <View style={styles.selectText}>
          {!compact ? <Text style={styles.selectLabel}>Sort</Text> : null}
          <Text numberOfLines={1} style={styles.selectValue}>
            {activeOption.label}
          </Text>
        </View>
        <ChevronDown color={colors.secondary} size={18} strokeWidth={2.5} />
      </Pressable>
      <Modal
        animationType="fade"
        transparent
        visible={menuVisible}
        onRequestClose={() => setMenuVisible(false)}
      >
        <Pressable
          style={styles.menuBackdrop}
          onPress={() => setMenuVisible(false)}
        >
          <Pressable
            style={styles.menuSheet}
            onPress={(event) => event.stopPropagation()}
          >
            <Text style={styles.menuTitle}>Comment sort</Text>
            <View style={styles.menuOptions}>
              {COMMENT_SORT_OPTIONS.map((option) => {
                const selected = option.value === value;

                return (
                  <Pressable
                    key={option.value}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    onPress={() => selectSortMode(option.value)}
                    style={({ pressed }) => [
                      styles.menuOption,
                      selected ? styles.menuOptionSelected : null,
                      pressed && !selected ? styles.menuOptionPressed : null,
                    ]}
                  >
                    <View style={styles.menuOptionText}>
                      <Text style={styles.menuOptionLabel}>{option.label}</Text>
                      <Text
                        numberOfLines={1}
                        style={styles.menuOptionDescription}
                      >
                        {option.description}
                      </Text>
                    </View>
                    {selected ? (
                      <Check
                        color={colors.primary}
                        size={20}
                        strokeWidth={2.7}
                      />
                    ) : null}
                  </Pressable>
                );
              })}
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  menuBackdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(29, 15, 70, 0.42)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.xl,
  },
  menuOption: {
    alignItems: 'center',
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.md,
    minHeight: 56,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  menuOptionDescription: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
  },
  menuOptionLabel: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  menuOptionPressed: {
    backgroundColor: colors.surfaceTint,
  },
  menuOptionSelected: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.primary,
  },
  menuOptionText: {
    flex: 1,
    gap: 2,
  },
  menuOptions: {
    gap: spacing.xs,
  },
  menuSheet: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    maxWidth: 420,
    padding: spacing.lg,
    width: '100%',
  },
  menuTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  selectButton: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    gap: spacing.xs,
    justifyContent: 'space-between',
    minHeight: 44,
    minWidth: 170,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
  },
  selectButtonCompact: {
    minHeight: 38,
    minWidth: 146,
    paddingHorizontal: spacing.sm,
  },
  selectButtonPressed: {
    backgroundColor: colors.surfaceTint,
  },
  selectLabel: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '800',
    textTransform: 'uppercase',
  },
  selectText: {
    flex: 1,
    gap: 1,
    minWidth: 0,
  },
  selectValue: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '900',
  },
});
